import { useCallback, useMemo } from 'react';
import { useLayers } from '@ais-ui/react-openlayers';
import { NextPortArrivalWithDate } from '@ais-ui/api';
import useRouteFetchAndDraw from './useRouteFetchAndDraw';

interface UseDrawRouteToPortProps {
  mmsi: string | undefined;
  shipName: string;
  nextport: NextPortArrivalWithDate;
  portName: string | undefined;
}

/**
 * Draws (or removes) a route line from a vessel to a candidate/arrival port
 * on the map, mirroring `RouteButton`'s behavior but exposed as a plain
 * toggle for use inside menu items instead of a standalone icon button.
 */
export const useDrawRouteToPort = ({
  mmsi,
  shipName,
  nextport,
  portName,
}: UseDrawRouteToPortProps) => {
  const { doesLayerExist, removeLayerById } = useLayers();

  const routeId = useMemo(() => {
    if (!mmsi) return '';
    const numberMmsi = parseInt(mmsi);
    const locode = nextport?.locode ? nextport.locode : '';
    return `${numberMmsi}-${locode}`;
  }, [mmsi, nextport]);

  const { onFetchRoute, isCalculatingRoute } = useRouteFetchAndDraw({
    mmsi,
    locode: nextport?.locode ? nextport.locode : '',
    shipName,
    portName,
  });

  const isRouteShown = doesLayerExist(routeId);

  const toggleRoute = useCallback(() => {
    if (isRouteShown) {
      removeLayerById(routeId);
    } else {
      onFetchRoute();
    }
  }, [isRouteShown, onFetchRoute, removeLayerById, routeId]);

  return {
    isRouteShown,
    toggleRoute,
    isCalculatingRoute,
    canDrawRoute: !!mmsi,
  };
};
